import { createFileRoute, useParams, notFound, Outlet } from '@tanstack/react-router'
import { useEffect, useState, useRef } from 'react'
import { Sidebar } from '../../components/chat/Sidebar'
import { ChatArea } from '../../components/chat/ChatArea'
import { Header } from '../../components/chat/Header'
import { ViewModeToggle } from '../../components/chat/ViewModeToggle'
import { useConversationStore } from '../../stores/conversationStore'
import { conversationAPI } from '../../services/api'
import { MessageSquare, Sparkles } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { cn } from '../../lib/utils'

type ViewMode = 'split' | 'chat' | 'preview'
type UIStyle = 'classic' | 'modern'

interface ChatLayoutSearchParams {
  slide?: string
  ui?: UIStyle
}

// NOTE: This is an example enhanced route. To use it, rename to $conversationId.tsx
// or register it properly in the route tree
export const Route = createFileRoute('/chat/$conversationId-enhanced')({
  validateSearch: (search: Record<string, unknown>): ChatLayoutSearchParams => {
    return {
      slide: search.slide as string | undefined,
      ui: (search.ui as UIStyle) || 'modern',
    }
  },
  component: EnhancedConversationLayout,
  loader: async ({ params }) => {
    // Validate that conversationId exists and is valid
    if (!params['conversationId-enhanced']) {
      throw notFound()
    }

    try {
      // Try to fetch the conversation to validate it exists
      const response = await conversationAPI.getById(params['conversationId-enhanced'])
      
      return {
        conversation: response.conversation
      }
    } catch (error) {
      // If conversation doesn't exist, throw notFound
      throw notFound()
    }
  },
})

function EnhancedConversationLayout() {
  const { conversationId } = useParams({ from: '/chat/$conversationId' })
  const { setActiveConversation, activeConversationId } = useConversationStore()
  // const searchParams = Route.useSearch() // Uncomment when properly registered
  
  const [sidebarVisible, setSidebarVisible] = useState(false)
  const [chatSidebarWidth, setChatSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('chatSidebarWidth')
    return saved ? parseInt(saved, 10) : 320
  })
  const [isResizing, setIsResizing] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('chatViewMode')
    return (saved as ViewMode) || 'split'
  })
  const [uiStyle, setUiStyle] = useState<UIStyle>(() => {
    const saved = localStorage.getItem('chatUIStyle')
    return (saved as UIStyle) || 'modern'
  })
  
  const sidebarRef = useRef<HTMLDivElement>(null)
  const resizeRef = useRef<HTMLDivElement>(null)

  // Set active conversation when route loads
  useEffect(() => {
    if (conversationId && conversationId !== activeConversationId) {
      setActiveConversation(conversationId)
    }
  }, [conversationId, setActiveConversation, activeConversationId])

  // Save UI style preference
  useEffect(() => {
    localStorage.setItem('chatUIStyle', uiStyle)
  }, [uiStyle])

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('chatViewMode', viewMode)
  }, [viewMode])

  // Save sidebar width
  useEffect(() => {
    localStorage.setItem('chatSidebarWidth', chatSidebarWidth.toString())
  }, [chatSidebarWidth])

  // Resize functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    
    const startX = e.clientX
    const startWidth = chatSidebarWidth

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.min(Math.max(startWidth + (e.clientX - startX), 280), 600)
      setChatSidebarWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // Toggle sidebar function
  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible)
  }

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarVisible && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setSidebarVisible(false)
      }
    }

    if (sidebarVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [sidebarVisible])

  const toggleUIStyle = () => {
    setUiStyle(prev => prev === 'classic' ? 'modern' : 'classic')
  }

  return (
    <div className={cn(
      "flex h-full bg-background relative overflow-hidden",
      isResizing && "select-none cursor-col-resize"
    )}>
      {/* Chat Sidebar */}
      <div
        ref={sidebarRef}
        className={cn(
          "absolute left-0 top-0 h-full border-r border-border bg-background/95 backdrop-blur-md transition-transform duration-300 ease-out z-[60] shadow-xl md:relative md:translate-x-0",
          sidebarVisible ? "translate-x-0" : "-translate-x-full md:hidden"
        )}
        style={{ width: `${chatSidebarWidth}px` }}
      >
        <div className="h-full flex flex-col">
          <div className="border-b border-border p-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Conversations</h3>
            <Button
              size="sm"
              variant="ghost"
              onClick={toggleUIStyle}
              className="h-7 px-2"
              title={`Switch to ${uiStyle === 'classic' ? 'modern' : 'classic'} UI`}
            >
              {uiStyle === 'classic' ? (
                <>
                  <Sparkles className="h-3.5 w-3.5 mr-1" />
                  Modern
                </>
              ) : (
                <>
                  <MessageSquare className="h-3.5 w-3.5 mr-1" />
                  Classic
                </>
              )}
            </Button>
          </div>
          <div className="flex-1 overflow-hidden">
            <Sidebar onChatSelect={() => setSidebarVisible(false)} />
          </div>
        </div>
        
        {/* Resize Handle */}
        <div
          ref={resizeRef}
          onMouseDown={handleMouseDown}
          className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors"
        />
      </div>
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-full min-w-0">
        {/* Header */}
        <Header 
          onToggleSidebar={toggleSidebar} 
          viewMode={viewMode}
        />

        {/* Content Based on View Mode */}
        <div className="flex-1 flex min-h-0">
          {viewMode === 'chat' ? (
            // Full Chat View
            // Both styles use the same modern ChatArea now
            (
              <ChatArea />
            )
          ) : viewMode === 'preview' ? (
            // Full Preview View
            (<div className="flex-1">
              <Outlet />
            </div>)
          ) : (
            // Split View
            (<>
              <div className="flex-1 border-r">
                <ChatArea />
              </div>
              <div className="flex-1">
                <Outlet />
              </div>
            </>)
          )}
        </div>

        {/* View Mode Toggle */}
        <div className="absolute bottom-4 right-4 z-50">
          <ViewModeToggle 
            viewMode={viewMode} 
            onViewModeChange={setViewMode}
          />
        </div>
      </div>
    </div>
  )
}